// The search palette behind the magnifying glass in every page header.
//
// One root, rendered once in the layout outside the Turbo frame, so it
// survives navigation. The glyph on a page opens it with that page's
// context (its controller name, and on a list page a function that applies
// the query to the list); `/` or ⌘K from anywhere does the same through the
// page's glyph, so the keyboard and the click are one path.
//
// Results come from GET /universal_search as groups, the page's own list
// first (see UniversalSearch). A row opens its record in place when the
// slideover for that type is on the page — or still loading, in which case
// lazySlideovers holds the event — and otherwise goes to the record's list
// and opens it there once the frame has loaded. A row that is a page of its
// own goes there; a row with nothing to open puts the query on its list.
import { pill } from '../utils/pills'

const MIN_QUERY = 2
const DEBOUNCE_MS = 150

// The slideover each event opens, by its Alpine component, so a page without
// that slideover can be told from one whose shell is still in flight.
const SLIDEOVER_FOR = {
  'open-property-slideover': 'propertySlideover',
  'open-tenancy-slideover': 'tenancySlideover',
  'open-person-slideover': 'personSlideover',
  'open-landlord-slideover': 'landlordSlideover',
  'open-contractor-slideover': 'contractorSlideover',
  'open-key-slideover': 'keySlideover',
  'open-issue-slideover': 'issueSlideover',
  'open-email-thread': 'emailThreadSlideover'
}

const slideoverOnPage = (event) => {
  const component = SLIDEOVER_FOR[event]
  if (component && document.querySelector(`[x-data^="${component}"]`)) return true
  // A lazy shell still loading carries the universal set; lazySlideovers
  // holds the event until it is listening.
  for (const container of window.lazySlideoversLoading || []) {
    if (container.isConnected && event !== 'open-email-thread') return true
  }
  return false
}

const typing = (target) => target && (
  target.tagName === 'TEXTAREA' || target.isContentEditable ||
  (target.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit'].includes(target.type))
)

export function universalSearch() {
  return {
    open: false,
    q: '',
    groups: [],
    items: [],
    sel: 0,
    loading: false,
    context: null,   // { context: 'properties', apply: fn|null }
    timer: null,
    inflight: null,
    seq: 0,

    init() {
      window.addEventListener('open-universal-search', (e) => this.show(e.detail || {}))
      window.addEventListener('keydown', (e) => this.globalKey(e))
      document.addEventListener('turbo:before-visit', () => { if (this.open) this.hide() })
    },

    // ---- opening and closing ----

    show(detail) {
      this.context = { context: detail.context || null, apply: typeof detail.apply === 'function' ? detail.apply : null }
      this.q = detail.q || ''
      this.groups = []
      this.items = []
      this.sel = 0
      this.open = true
      this.$nextTick(() => {
        this.$refs.input?.focus()
        if (this.q.trim().length >= MIN_QUERY) this.fetch()
      })
    },

    hide() {
      this.open = false
      this.inflight?.abort()
      clearTimeout(this.timer)
      this.loading = false
    },

    // `/` or ⌘K anywhere: open through the page's own glyph so the context
    // and the list's apply function come from the page, exactly as a click.
    globalKey(e) {
      const cmdK = (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k'
      const slash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey
      if (!cmdK && !slash) return
      if (this.open) return
      if (slash && typing(e.target)) return
      e.preventDefault()
      const trigger = document.querySelector('[data-universal-search-trigger]')
      if (trigger) trigger.click()
      else this.show({})
    },

    // ---- searching ----

    onInput() {
      clearTimeout(this.timer)
      const query = this.q.trim()
      if (query.length < MIN_QUERY) {
        this.inflight?.abort()
        this.loading = false
        this.groups = []
        this.items = []
        return
      }
      this.timer = setTimeout(() => this.fetch(), DEBOUNCE_MS)
    },

    async fetch() {
      const query = this.q.trim()
      if (query.length < MIN_QUERY) return
      this.inflight?.abort()
      const controller = new AbortController()
      this.inflight = controller
      const seq = ++this.seq
      this.loading = true
      try {
        const params = new URLSearchParams({ q: query })
        if (this.context?.context) params.set('context', this.context.context)
        const response = await fetch(`/universal_search?${params}`, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (seq !== this.seq) return   // a later keystroke has overtaken this one
        this.groups = data.groups || []
        this.items = this.flatten(this.groups)
        this.sel = 0
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[UniversalSearch]', error)
      } finally {
        if (seq === this.seq) this.loading = false
      }
    },

    // Every row across every group as one list for the arrow keys, with the
    // "Show in the list" line at the foot of the page's own group.
    flatten(groups) {
      const items = []
      groups.forEach((group) => {
        group.rows.forEach((row) => items.push({ kind: 'open', row }))
        if (group.here && this.context?.apply) items.push({ kind: 'list', total: group.total })
      })
      return items
    },

    indexOf(group, rowIndex) {
      let i = 0
      for (const g of this.groups) {
        if (g === group) return i + rowIndex
        i += g.rows.length + (g.here && this.context?.apply ? 1 : 0)
      }
      return -1
    },

    listIndex(group) {
      return this.indexOf(group, group.rows.length)
    },

    get empty() {
      return !this.loading && this.q.trim().length >= MIN_QUERY && this.groups.length === 0
    },

    get listLine() {
      const total = this.groups.find((g) => g.here)?.total
      if (total === undefined || total === null) return ''
      return total === 1 ? 'Show the 1 match in the list' : `Show all ${total} matches in the list`
    },

    // ---- keys inside the field ----

    inputKey(e) {
      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          if (this.q) { this.q = ''; this.onInput() } else this.hide()
          break
        case 'ArrowDown':
          e.preventDefault()
          if (this.items.length) { this.sel = (this.sel + 1) % this.items.length; this.scrollToSel() }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (this.items.length) { this.sel = (this.sel - 1 + this.items.length) % this.items.length; this.scrollToSel() }
          break
        case 'Enter':
          e.preventDefault()
          this.choose(this.sel)
          break
      }
    },

    scrollToSel() {
      this.$nextTick(() => this.$root.querySelector(`[data-sel="${this.sel}"]`)?.scrollIntoView({ block: 'nearest' }))
    },

    // ---- choosing ----

    choose(index) {
      const item = this.items[index]
      if (!item) return
      if (item.kind === 'list') {
        const query = this.q.trim()
        const apply = this.context?.apply
        this.hide()
        apply?.(query)
        return
      }
      this.openRow(item.row)
    },

    openRow(row) {
      const query = this.q.trim()
      const apply = this.context?.apply
      this.hide()
      // A row that is a page of its own (a letter) goes there.
      if (row.navigate) {
        window.Turbo.visit(row.navigate, { frame: 'main', action: 'advance' })
        return
      }
      // A row with nothing to open (a user, an inspection) puts the query on
      // the list it came from, so the row is on the page in front of you.
      if (!row.event) {
        if (apply && this.onPage(row.href)) apply(query)
        else this.visitThenApply(row.href, query)
        return
      }
      if (slideoverOnPage(row.event) || (!SLIDEOVER_FOR[row.event] && this.onPage(row.href))) {
        window.lazySlideovers.open(row.event, row.detail)
        return
      }
      // The record's own list, then its slideover once the frame has drawn.
      this.afterFrameLoad(() => window.lazySlideovers.open(row.event, row.detail))
      window.Turbo.visit(row.href, { frame: 'main', action: 'advance' })
    },

    onPage(href) {
      return window.location.pathname === href
    },

    afterFrameLoad(fn) {
      const once = (e) => {
        if (e.target?.id !== 'main') return
        document.removeEventListener('turbo:frame-load', once)
        setTimeout(fn, 50)
      }
      document.addEventListener('turbo:frame-load', once)
    },

    // Go to the list and search it there: the page's own glyph carries the
    // list's apply, so click it with the query and take its "Show in the
    // list" line — the same path a person would take.
    visitThenApply(href, query) {
      this.afterFrameLoad(() => {
        const trigger = document.querySelector('[data-universal-search-trigger]')
        if (!trigger) return
        trigger.click()
        this.q = query
        this.$nextTick(() => {
          const apply = this.context?.apply
          this.hide()
          apply?.(query)
        })
      })
      window.Turbo.visit(href, { frame: 'main', action: 'advance' })
    },

    pillClass(tone) {
      return pill(tone)
    }
  }
}
